import { Box, Chip, Paper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import type { OpenTaskRow } from "../types";

const Root = styled(Paper)(({ theme }) => ({ padding: theme.spacing(2, 2.5) }));

const Heading = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
}));

const Title = styled(Typography)(({ theme }) => ({ ...theme.typography.h2 }));

const Task = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  padding: theme.spacing(1, 0),
  borderTop: `1px solid ${theme.palette.divider}`,
  "&:first-of-type": { borderTop: "none" },
}));

const Label = styled(Typography)(() => ({ fontSize: "0.875rem", fontWeight: 600 }));

const Detail = styled(Typography)(({ theme }) => ({ fontSize: "0.8125rem", color: theme.palette.text.secondary }));

const Empty = styled(Typography)(({ theme }) => ({ color: theme.palette.text.secondary, fontSize: "0.875rem" }));

const READINESS: Record<string, { label: string; color: "default" | "warning" | "success" }> = {
  not_ready: { label: "Not ready", color: "default" },
  // Signing off comes once the mission has ended (SignOff): until then the handoff is the AI's draft.
  draft_available: { label: "AI draft", color: "warning" },
  reviewed: { label: "Signed off", color: "success" },
};

type OpenTasksPanelProps = {
  tasks: OpenTaskRow[];
  missingFields: string[];
  handoffReadiness: string;
};

/** Workflow state the reducer derived - not tasks a model invented. */
export function OpenTasksPanel(props: OpenTasksPanelProps) {
  const readiness = READINESS[props.handoffReadiness] ?? {
    label: props.handoffReadiness,
    color: "default" as const,
  };
  return (
    <Root>
      <Heading>
        <Title>Open tasks</Title>
        <Chip size="small" color={readiness.color} label={readiness.label} />
      </Heading>
      {props.tasks.length === 0 && props.missingFields.length === 0 ? <Empty>Nothing outstanding.</Empty> : null}
      {props.tasks.map((task) => (
        <Task key={task.key}>
          <Label>{task.label}</Label>
          {task.detail ? <Detail>{task.detail}</Detail> : null}
        </Task>
      ))}
    </Root>
  );
}
